import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import RichTextEditor from '../components/blog/RichTextEditor'
import MetaTags from '../components/common/MetaTags'
import WheelLoader from '../components/common/WheelLoader'
import { pagesService } from '../services/pagesService'
import type { Page, PageInput } from '../types'

export default function PageEditorPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = Boolean(id)

  const [formData, setFormData] = useState<PageInput>({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    icon: '',
    status: 'draft',
    is_public: true,
    show_in_nav: true,
  })
  const [page, setPage] = useState<Page | null>(null)
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    if (isEditing && id) {
      const fetchPage = async () => {
        try {
          const data = await pagesService.getPage(Number(id))
          setPage(data)
          setFormData({
            title: data.title,
            slug: data.slug,
            content: data.content || '',
            meta_description: data.meta_description || '',
            icon: data.icon || '',
            status: data.status,
            is_public: data.is_public,
            show_in_nav: data.show_in_nav,
          })
        } catch {
          setError('Failed to load page')
        } finally {
          setLoading(false)
        }
      }
      fetchPage()
    }
  }, [id, isEditing])

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      setError('Title is required')
      return
    }

    setSaving(true)
    setError('')
    try {
      let savedPage: Page
      if (isEditing && id) {
        savedPage = await pagesService.updatePage(Number(id), formData)
        showSuccess('Page saved!')
      } else {
        savedPage = await pagesService.createPage(formData)
        navigate(`/pages/edit/${savedPage.id}`, { replace: true })
        showSuccess('Page created!')
      }
      setPage(savedPage)
      setFormData((prev) => ({ ...prev, slug: savedPage.slug }))
    } catch {
      setError('Failed to save page')
    } finally {
      setSaving(false)
    }
  }

  const handlePublishToggle = async () => {
    if (!page) return
    setSaving(true)
    setError('')
    try {
      const updatedPage =
        page.status === 'published'
          ? await pagesService.unpublishPage(page.id)
          : await pagesService.publishPage(page.id)
      setPage(updatedPage)
      setFormData((prev) => ({ ...prev, status: updatedPage.status }))
      showSuccess(updatedPage.status === 'published' ? 'Page published!' : 'Page unpublished')
    } catch {
      setError('Failed to update publish status')
    } finally {
      setSaving(false)
    }
  }

  const handleContentChange = useCallback((content: string) => {
    setFormData((prev) => ({ ...prev, content }))
  }, [])

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <MetaTags title="Loading... | profile.work" description="Loading page" />
        <div className="text-center py-12">
          <div className="mx-auto mb-4">
            <WheelLoader width={80} />
          </div>
          <p className="text-black/60">Loading page...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <MetaTags
        title={isEditing ? 'Edit Page | profile.work' : 'New Page | profile.work'}
        description="Create or edit a custom page"
      />

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
          <div className="px-4 py-3 bg-black/90 backdrop-blur-xl rounded-xl shadow-lg">
            <p className="text-sm text-white flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <button
          onClick={() => navigate('/pages')}
          className="text-black/60 hover:text-black flex items-center gap-1 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Pages
        </button>
        <div className="flex gap-2 w-full sm:w-auto">
          {page && (
            <button
              onClick={handlePublishToggle}
              disabled={saving}
              className={`flex-1 sm:flex-none px-4 py-2 text-sm font-medium rounded-xl transition-all disabled:opacity-50 ${
                page.status === 'published'
                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {page.status === 'published' ? 'Unpublish' : 'Publish'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 shadow-sm"
          >
            {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 mb-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Title */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
          <input
            type="text"
            placeholder="Page Title"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full text-2xl sm:text-3xl font-bold border-0 py-5 px-6 focus:outline-none focus:ring-0 bg-transparent"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          />
        </div>

        {/* Page Settings */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-black/70 mb-1">URL Slug</label>
              <div className="flex items-center">
                <span className="text-sm text-black/40 mr-1">/pages/</span>
                <input
                  type="text"
                  placeholder="page-url-slug"
                  value={formData.slug || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    }))
                  }
                  className="flex-1 border border-gray-300 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
              <p className="text-xs text-black/40 mt-1">Leave blank to auto-generate from title</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-black/70 mb-1">Icon (emoji)</label>
              <input
                type="text"
                placeholder="e.g. About"
                value={formData.icon || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, icon: e.target.value }))}
                className="w-full border border-gray-300 rounded-xl py-2 px-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
              <p className="text-xs text-black/40 mt-1">Optional emoji to display in nav</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-black/70 mb-1">Meta Description</label>
            <textarea
              placeholder="Brief description for SEO (max 160 characters)..."
              value={formData.meta_description || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, meta_description: e.target.value }))
              }
              rows={2}
              maxLength={160}
              className="w-full border border-gray-300 rounded-xl py-3 px-4 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
            />
            <p className="text-xs text-black/40 mt-1">
              {(formData.meta_description || '').length}/160 characters
            </p>
          </div>

          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.show_in_nav}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, show_in_nav: e.target.checked }))
                }
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-black/70">Show in navigation</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_public}
                onChange={(e) => setFormData((prev) => ({ ...prev, is_public: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-black/70">Public page</span>
            </label>
          </div>
        </div>

        {/* Rich Text Editor */}
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
          <RichTextEditor content={formData.content || ''} onChange={handleContentChange} />
        </div>
      </div>
    </div>
  )
}
